import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Date the legal text was last reviewed. Kept as a plain ISO string so it can
// be formatted in the user's locale at render time.
const LAST_UPDATED_ISO = '2026-04-18';

const styles = {
  page: {
    minHeight: '100dvh',
    background: '#0F172A',
    color: '#F8FAFC',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    padding: '3rem 1.5rem',
  },
  container: {
    maxWidth: 720,
    margin: '0 auto',
  },
  backLink: {
    color: '#60A5FA',
    textDecoration: 'none',
    fontSize: '0.9rem',
    display: 'inline-block',
    marginBottom: '2rem',
  },
  h1: {
    fontSize: '2rem',
    fontWeight: 800,
    color: '#F8FAFC',
    marginBottom: '0.5rem',
  },
  updated: {
    color: '#94A3B8',
    fontSize: '0.85rem',
    marginBottom: '2.5rem',
  },
  h2: {
    fontSize: '1.25rem',
    fontWeight: 700,
    color: '#F8FAFC',
    marginTop: '2rem',
    marginBottom: '0.75rem',
  },
  p: {
    color: '#CBD5E1',
    lineHeight: 1.7,
    marginBottom: '1rem',
    fontSize: '0.95rem',
  },
  ul: {
    color: '#CBD5E1',
    lineHeight: 1.7,
    paddingLeft: '1.5rem',
    marginBottom: '1rem',
    fontSize: '0.95rem',
  },
  link: {
    color: '#60A5FA',
    textDecoration: 'none',
  },
};

export default function TermsPage() {
  const { t, i18n } = useTranslation();
  const lastUpdated = new Date(LAST_UPDATED_ISO).toLocaleDateString(i18n.language, {
    year: 'numeric', month: 'long', day: 'numeric',
  });

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <Link to="/" style={styles.backLink}>{t('legal.back')}</Link>

        <h1 style={styles.h1}>{t('legal.terms.title')}</h1>
        <p style={styles.updated}>{t('legal.lastUpdated', { date: lastUpdated })}</p>

        {/* The legal body below is intentionally English-only. Translating
            terms of service requires per-jurisdiction legal review; until
            that's commissioned, the body is not wrapped in t(). */}

        <h2 style={styles.h2}>1. Acceptance of Terms</h2>
        <p style={styles.p}>
          By accessing or using the vQueue platform ("Service"), you agree to be bound by these
          Terms of Service ("Terms"). If you do not agree, do not use the Service.
        </p>

        <h2 style={styles.h2}>2. Service Description</h2>
        <p style={styles.p}>
          vQueue is a business-to-business-to-consumer (B2B2C) virtual queue management platform.
          Businesses ("Tenants") create and manage one or more offices and queues. Customers
          ("End Users") join queues by scanning a QR code or using a physical kiosk at the
          business location, without needing to create an account. Staff may additionally operate
          the queue from a dedicated staff dashboard or a waiting-room display screen.
        </p>

        <h2 style={styles.h2}>3. Accounts</h2>
        <p style={styles.p}>
          Business accounts are created by providing a valid email address, which is verified via
          a single-use email link before the account is activated. You then set a password. You
          are responsible for maintaining the confidentiality of your credentials and for all
          activity under your account. You must notify us immediately of any unauthorized use.
        </p>
        <p style={styles.p}>
          Staff members are added via email invitation. Invitees accept the invite through a
          single-use link and set their own password. Account owners may revoke invites or
          remove staff members at any time.
        </p>
        <p style={styles.p}>
          End Users do not create accounts. Temporary queue participation requires only a first
          name and optional emoji avatar; walk-up tickets issued at a kiosk are fully anonymous.
        </p>

        <h2 style={styles.h2}>4. Acceptable Use</h2>
        <p style={styles.p}>You agree not to:</p>
        <ul style={styles.ul}>
          <li>Use the Service for any unlawful purpose or in violation of any applicable laws</li>
          <li>Interfere with or disrupt the integrity or performance of the Service</li>
          <li>Attempt to gain unauthorized access to the Service or its related systems</li>
          <li>Use automated tools to scrape, crawl, or extract data from the Service</li>
          <li>Impersonate another person or entity</li>
          <li>Transmit malicious code, spam, or harmful content through the Service</li>
        </ul>

        <h2 style={styles.h2}>5. Intellectual Property</h2>
        <p style={styles.p}>
          The Service, including its design, features, and content, is owned by vQueue and
          protected by intellectual property laws. You may not copy, modify, distribute, or
          reverse-engineer any part of the Service without prior written consent.
        </p>

        <h2 style={styles.h2}>6. Plans, Payment and Billing</h2>
        <p style={styles.p}>
          New accounts start on the free <strong>Solo</strong> plan. Paid plans
          (<strong>High Street</strong> and <strong>Chain</strong>) unlock additional offices, staff
          seats, extended analytics retention, and features such as dynamic kiosk QR codes,
          heatmap analytics, and CSV export. Per-plan limits are displayed in the pricing page
          and may be updated from time to time; changes that reduce limits will not apply
          retroactively to an active billing period.
        </p>
        <p style={styles.p}>
          Paid plans are billed in advance through Stripe on the billing cadence selected at
          checkout. All fees are non-refundable except as required by law. We reserve the right
          to change pricing with at least 30 days' notice given by email; continued use after the
          change takes effect constitutes acceptance of the new pricing. If payment fails, Stripe
          will retry automatically; if payment cannot be collected, your account may be
          downgraded to the Solo plan and affected paid features will become unavailable.
          Cancellation takes effect at the end of the current billing period, and no refund is
          issued for the unused portion.
        </p>

        <h2 style={styles.h2}>7. Availability and Uptime</h2>
        <p style={styles.p}>
          We strive to maintain high availability but do not guarantee uninterrupted access.
          The Service may be temporarily unavailable for maintenance, updates, or circumstances
          beyond our control.
        </p>

        <h2 style={styles.h2}>8. Disclaimers</h2>
        <p style={styles.p}>
          The Service is provided "as is" and "as available" without warranties of any kind,
          whether express or implied, including but not limited to implied warranties of
          merchantability, fitness for a particular purpose, and non-infringement.
        </p>

        <h2 style={styles.h2}>9. Limitation of Liability</h2>
        <p style={styles.p}>
          To the maximum extent permitted by law, vQueue shall not be liable for any indirect,
          incidental, special, consequential, or punitive damages, or any loss of profits or
          revenue, whether incurred directly or indirectly, arising from your use of the Service.
          Our total liability shall not exceed the amount you paid us in the 12 months preceding
          the claim.
        </p>

        <h2 style={styles.h2}>10. Termination</h2>
        <p style={styles.p}>
          We may suspend or terminate your access at any time for violation of these Terms or
          for any other reason at our discretion, with or without notice. Platform operators may
          freeze a tenant, office, or user account pending investigation of suspected abuse or
          non-payment; frozen accounts cannot accept new queue participants until unfrozen. You
          may terminate your account at any time by contacting us. Upon termination, your right
          to use the Service ceases immediately; we will delete or anonymize your data in
          accordance with our Privacy Policy.
        </p>

        <h2 style={styles.h2}>11. Governing Law</h2>
        <p style={styles.p}>
          These Terms are governed by and construed in accordance with the laws of the European
          Union and applicable member state legislation. Any disputes shall be resolved in the
          competent courts of the applicable jurisdiction.
        </p>

        <h2 style={styles.h2}>12. Changes to These Terms</h2>
        <p style={styles.p}>
          We may update these Terms from time to time. We will notify registered users of
          material changes via email or through the Service. Continued use after changes
          constitutes acceptance of the updated Terms.
        </p>

        <h2 style={styles.h2}>13. Contact</h2>
        <p style={styles.p}>
          If you have questions about these Terms, contact us
          at <a href="mailto:legal@waitless.live" style={styles.link}>legal@waitless.live</a>.
        </p>

        <p style={{ ...styles.p, marginTop: '2rem', borderTop: '1px solid #1E293B', paddingTop: '1.5rem' }}>
          See also our <Link to="/privacy" style={styles.link}>Privacy Policy</Link>.
        </p>
      </div>
    </div>
  );
}
